/* vld-product.js - behaviour of sections/vld-product.liquid (VickersLDN wallet page with the vector editor).
 *
 * Reads its config from <script type="application/json" data-vld-config> inside [data-vld-product]:
 *   {sectionId, hub, designId, baseId, comboId, product (Shopify product JSON, prices in minor units), currency,
 *    productTitle, cdnBase, productId, swatches: [{colour, image, variantId}], gift: {price, compareAt, variants: [...]},
 *    giftFallback: {price, variants: [...]}, initialColour}
 * Needs vld-design.js + vld-editor.js (VLDEditor.mount) loaded before it.
 *
 * Cart contract (docs/kb/16): one wallet line, variant chosen by option NAME (Color / Inside Engraving / Back
 * Engraving), properties Front, Inside Engraving, Back Engraving, Gift Box, _design_id, _design_version, _combo_id,
 * _overrides (JSON), _preview_url; the gift box is its own line (the real gift-box variant).
 */
(function () {
  'use strict';
  const DESKTOP = '(min-width: 990px)';

  function init(root) {
    const cfgEl = root.querySelector('[data-vld-config]');
    if (!cfgEl) return;
    const cfg = JSON.parse(cfgEl.textContent);
    const product = cfg.product;
    const $ = (s, el) => (el || root).querySelector(s);
    const $$ = (s, el) => Array.prototype.slice.call((el || root).querySelectorAll(s));
    const money = c => (c / 100).toLocaleString('en-GB', { style: 'currency', currency: cfg.currency || 'GBP' });
    const norm = s => String(s || '').trim().toLowerCase().replace('hazlenut', 'hazelnut');
    // swatch discs cut from the real leather photos (hub /pub/swatches/<base>.json); null until loaded
    let hubSwatches = null;
    if (cfg.hub && window.fetch) {
      fetch(cfg.hub.replace(/\/$/, '') + '/pub/swatches/' + encodeURIComponent(cfg.baseId || '34') + '.json')
        .then(r => (r.ok ? r.json() : null))
        .then(map => {
          if (!map) return;
          hubSwatches = {};
          Object.keys(map).forEach(k => { if (map[k] && map[k].image) hubSwatches[norm(k)] = map[k].image; });
          if (typeof renderSwatches === 'function') renderSwatches();
        }).catch(() => {});
    }
    const mq = window.matchMedia(DESKTOP);

    // ---------- options by NAME (the Inside/Back-swapped bug in the old code came from fixed positions) ----------
    const optIndex = name => product.options.findIndex(o => norm(o.name || o) === norm(name));
    const iColour = optIndex('Color') >= 0 ? optIndex('Color') : optIndex('Colour');
    const iInside = optIndex('Inside Engraving');
    const iBack = optIndex('Back Engraving');
    const yes = v => /^(yes|true|1)$/i.test(String(v || '').trim());
    // any other option (Size on the keepsake box, Capacity...): buttons, matched by name (19 Oct 2026, KB 24)
    const extras = (product.options || []).map((o, i) => ({ name: o.name || o, i }))
      .filter(o => o.i !== iColour && o.i !== iInside && o.i !== iBack)
      .map(o => ({ ...o, values: [...new Set(product.variants.map(v => v.options[o.i]))] }));
    const extraSel = {};

    function findVariant(colour, inside, back) {
      return product.variants.find(v =>
        (iColour < 0 || norm(v.options[iColour]) === norm(colour)) &&
        (iInside < 0 || yes(v.options[iInside]) === !!inside) &&
        (iBack < 0 || yes(v.options[iBack]) === !!back) &&
        extras.every(o => v.options[o.i] === extraSel[o.name])) || null;
    }

    // ---------- gift box: the real product via all_products, else the known variant ids ----------
    const gift = (cfg.gift && cfg.gift.variants && cfg.gift.variants.length) ? cfg.gift : cfg.giftFallback;

    // ---------- state ----------
    const first = product.variants.find(v => v.available) || product.variants[0];
    const state = {
      colour: cfg.initialColour || (iColour >= 0 ? first.options[iColour] : ''),
      inside: false, back: false, gift: false,
      giftVariant: gift && gift.variants.length ? gift.variants[0] : null,
    };
    let editor = null, editorLive = false, fontsCat = null;
    extras.forEach(o => { extraSel[o.name] = first.options[o.i]; });
    function renderOptions() {
      $$('[data-vld-options]').forEach(box => {
        box.innerHTML = '';
        extras.forEach(o => {
          const g = document.createElement('div'); g.className = 'vld-opt';
          g.innerHTML = '<span class="lux-label">' + o.name + ': <span data-vld-opt-name></span></span><div class="vld-choices"></div>';
          g.querySelector('[data-vld-opt-name]').textContent = extraSel[o.name];
          o.values.forEach(val => {
            const b = document.createElement('button');
            b.type = 'button'; b.className = 'vld-choice' + (val === extraSel[o.name] ? ' on' : '');
            b.textContent = val;
            b.addEventListener('click', () => {
              extraSel[o.name] = val; renderOptions(); refresh();
              const variant = findVariant(state.colour, state.inside, state.back);
              document.dispatchEvent(new CustomEvent('luxury:variantChange', { detail: { variant: variant, colour: state.colour, options: variant ? variant.options : null } }));
            });
            g.querySelector('.vld-choices').appendChild(b);
          });
          box.appendChild(g);
        });
      });
    }

    // ---------- media: hero picture until the editor renders, then its canvas ----------
    const media = $('.luxury-media');
    const heroImg = $('.luxury-media__img');
    const heroBackdrop = $('.luxury-media__backdrop');
    const stageSlot = $('.vld-stage');
    const stageBg = $('.vld-stage__bg');
    const sheetPreview = $('.vld-sheet__preview');
    const sheetCanvas = $('.vld-sheet__canvas');
    const sheetBg = $('.vld-sheet__bg');
    const sheetImg = $('.vld-sheet__preview img');

    const heroUrls = colour => ({
      desktop: cfg.cdnBase + cfg.productId + '_' + norm(colour) + '_5376-3072.jpg',
      mobile: cfg.cdnBase + cfg.productId + '_' + norm(colour) + '_1080-1920.jpg',
    });

    function swapImage(urls) {
      if (!heroImg) return;
      const picture = heroImg.closest('picture');
      const sources = picture ? picture.querySelectorAll('source') : [];
      const newSrc = mq.matches ? urls.desktop : urls.mobile;
      const pre = new Image();
      const apply = () => {
        heroImg.classList.add('is-fading');
        setTimeout(() => {
          if (sources.length >= 2) { sources[0].srcset = urls.mobile; sources[1].srcset = urls.desktop; }
          heroImg.src = newSrc;
          if (heroBackdrop) heroBackdrop.src = urls.desktop;
          if (sheetImg) sheetImg.src = urls.mobile;
          heroImg.classList.remove('is-fading');
        }, 150);
      };
      pre.onload = apply; pre.onerror = apply;
      pre.src = newSrc;
    }

    function fadeUpdate(img, src) {
      if (!img) return;
      img.classList.add('is-fading');
      const t = new Image();
      t.onload = () => { img.src = src; img.classList.remove('is-fading'); };
      t.onerror = () => img.classList.remove('is-fading');
      t.src = src;
    }

    function updateCraftsmanship(colour) {
      if (cfg.isWallet === false) return;   // the inside / back tiles exist on the wallet only
      const c = norm(colour);
      fadeUpdate($('[data-vld-craft="inside-desktop"]'), cfg.cdnBase + c + '_inside_5376-3072.jpg');
      fadeUpdate($('[data-vld-craft="back-desktop"]'), cfg.cdnBase + c + '_back_5376-3072.jpg');
      fadeUpdate($('[data-vld-craft="inside-mobile"]'), cfg.cdnBase + c + '_inside_768-1376.jpg');
      fadeUpdate($('[data-vld-craft="back-mobile"]'), cfg.cdnBase + c + '_back_768-1376.jpg');
    }

    const srcSize = s => ({ w: s.naturalWidth || s.width, h: s.naturalHeight || s.height });
    function copyInto(c, src, blur) {
      // draw src (canvas or image) into c at src's size; blur = the soft backdrop: zoomed 12% and blurred inside its
      // own pixels (a CSS transform/filter would push the box out of the column and fade its edges)
      const { w, h } = srcSize(src);
      if (!w) return;
      if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
      const ctx = c.getContext('2d');
      if (blur) { ctx.filter = 'blur(60px)'; ctx.drawImage(src, -w * 0.06, -h * 0.06, w * 1.12, h * 1.12); ctx.filter = 'none'; }
      else ctx.drawImage(src, 0, 0, w, h);
    }
    function mirrorCanvas() {
      // the editor owns one canvas (the hero); the blurred backdrop, the mobile sheet preview and the Front thumbnail copy it
      const main = stageSlot && stageSlot.querySelector('.vld-ed__product');
      if (!main || !main.width) return;
      if (stageBg) copyInto(stageBg, main, true);
      if (sheetBg) copyInto(sheetBg, main, true);
      if (sheetCanvas) copyInto(sheetCanvas, main, false);
      paintThumb('front', main);
    }

    // ---------- swatches (same markup in the desktop column, the mobile bar and the sheet) ----------
    function renderSwatches() {
      $$('[data-vld-swatches]').forEach(box => {
        box.innerHTML = '';
        cfg.swatches.forEach(s => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'lux-swatch' + (norm(s.colour) === norm(state.colour) ? ' is-selected' : '');
          b.dataset.colour = s.colour;
          b.setAttribute('aria-label', 'Select ' + s.colour);
          b.title = s.colour;
          // disc = a crop of the real leather photo (hub /pub/swatches), else the variant image, else the name as a colour
          const hubSw = hubSwatches && hubSwatches[norm(s.colour)];
          if (hubSw) b.style.backgroundImage = 'url("' + hubSw + '")';
          else if (s.image) b.style.backgroundImage = 'url("' + s.image + '")';
          else b.style.backgroundColor = s.colour;
          b.addEventListener('click', () => selectColour(s.colour));
          box.appendChild(b);
        });
      });
      $$('[data-vld-colour-name]').forEach(e => { e.textContent = state.colour; });
    }

    function recipeColour(colour) {
      // the editor's colour keys come from recipes.json; match the Shopify option value case-insensitively
      if (!editor) return colour;
      const keys = editor.colours ? editor.colours() : [];
      return keys.find(k => norm(k) === norm(colour)) || colour;
    }

    function selectColour(colour) {
      state.colour = colour;
      $$('.lux-swatch').forEach(b => b.classList.toggle('is-selected', norm(b.dataset.colour) === norm(colour)));
      $$('[data-vld-colour-name]').forEach(e => { e.textContent = colour; });
      const urls = heroUrls(colour);
      if (editorLive) editor.setProduct({ colour: recipeColour(colour) });
      else swapImage(urls);
      updateCraftsmanship(colour);
      recolourViews();
      refresh();
      const variant = findVariant(colour, state.inside, state.back);
      document.dispatchEvent(new CustomEvent('luxury:variantChange', {
        detail: { variant: variant, colour: colour, options: variant ? variant.options : null },
      }));
    }

    // ---------- prices: everything from the variants; add-ons = difference vs the No/No variant ----------
    function priceOf(inside, back) {
      const v = findVariant(state.colour, inside, back);
      return v ? v.price : null;
    }

    function refresh() {
      const v = findVariant(state.colour, state.inside, state.back) || findVariant(state.colour, false, false) || first;
      let total = v.price, compare = v.compare_at_price || v.price;
      if (state.gift && state.giftVariant) { total += state.giftVariant.price; compare += state.giftVariant.compare_at_price || state.giftVariant.price; }
      // Shopify's compare-at is the same £40 on every variant, so once add-ons are in it can sit 1p above the price: only strike through a real saving
      const compareText = compare >= total * 1.05 ? money(compare) : '';
      $$('[data-vld-price]').forEach(e => { e.textContent = money(total); });
      $$('[data-vld-compare]').forEach(e => { e.textContent = compareText; });
      const base = priceOf(false, false), pi = priceOf(true, false), pb = priceOf(false, true);
      const label = (p) => (p === null || base === null) ? '' : (p > base ? '+' + money(p - base) : 'Included');
      const ins = $('[data-addon="inside"] [data-addon-price]'), bk = $('[data-addon="back"] [data-addon-price]');
      if (ins) ins.textContent = label(pi);
      if (bk) bk.textContent = label(pb);
      const gp = $('[data-addon="gift"] [data-addon-price]');
      if (gp && state.giftVariant) gp.textContent = '+' + money(state.giftVariant.price);
      // the collapsed add-ons row (phones): what is on, else what is available
      const names = { inside: 'Inside engraving', back: 'Back engraving', gift: 'Gift box' };
      const offered = ['inside', 'back', 'gift'].filter(k => { const b = $('[data-addon="' + k + '"]'); return b && !b.hidden; });
      const on = offered.filter(k => state[k]).map(k => names[k]);
      $$('[data-addon-summary]').forEach(e => {
        e.textContent = on.length ? on.join(' · ') : offered.map(k => names[k].replace(' engraving', '')).join(' · ');
        e.classList.toggle('has-some', on.length > 0);
      });
      $$('[data-vld-atc]').forEach(b => { b.disabled = false; b.querySelector('span').textContent = 'Add to cart · ' + money(total); });
      $$('[data-vld-open-sheet] span').forEach(s => { s.textContent = 'Personalise · ' + money(total); });
    }

    // ---------- add-ons ----------
    $$('.vld-addon').forEach(box => {
      const kind = box.dataset.addon, toggle = $('[data-toggle]', box), body = $('.vld-addon__body', box);
      const text = $('input[type=text]', box), count = $('[data-count]', box);
      if (kind === 'inside' && iInside < 0) { box.hidden = true; return; }
      if (kind === 'back' && iBack < 0) { box.hidden = true; return; }
      if (kind === 'gift' && !(gift && gift.variants.length)) { box.hidden = true; return; }
      toggle.addEventListener('change', () => {
        state[kind] = toggle.checked;
        if (body) body.hidden = !toggle.checked;
        if (toggle.checked && text) setTimeout(() => { text.focus({ preventScroll: true }); if (!mq.matches) text.scrollIntoView({ block: 'nearest' }); }, 50);   // the field sits right under the tick: no page jump on desktop
        refresh();
        if (kind === 'inside' || kind === 'back') { renderView(kind); if (toggle.checked) setView(kind); }
        if (kind === 'gift' && toggle.checked && giftImage()) setView('gift');
      });
      let typing = null;
      if (text && count) text.addEventListener('input', () => {
        count.textContent = text.value.length + '/' + text.maxLength;
        if (kind === 'inside' || kind === 'back') {
          if (views.current !== kind) setView(kind);
          clearTimeout(typing); typing = setTimeout(() => renderView(kind), 300);
        }
      });
      $$('[data-gift-variant]', box).forEach(b => b.addEventListener('click', () => {
        $$('[data-gift-variant]', box).forEach(x => x.classList.remove('on'));
        b.classList.add('on');
        state.giftVariant = gift.variants.find(g => String(g.id) === b.dataset.giftVariant) || state.giftVariant;
        refresh();
        if (giftImage()) { $$('[data-vld-views] [data-view="gift"] img').forEach(i => { i.src = giftThumbnail(); }); if (views.current === 'gift') paintHero('gift'); }
      }));
    });

    // ---------- the 4 views: Front (the live canvas), Inside / Back (the colour's photo, engraved live with the add-on
    // text: recipes.json bases[base].inside|back[colour] gives the plate + box; without one the CDN photo and a default
    // box are used), Gift box (the gift product's image, hidden when there is none) ----------
    const GALLERY = (cfg.gallery || []).filter(g => g && g.url);   // extra real photos ({pid}_gallery_*), e.g. inside the box
    const VIEWS = ['front', 'inside', 'back', 'gift'].filter(v => (v !== 'inside' || iInside >= 0) && (v !== 'back' || iBack >= 0) &&
      (v !== 'gift' || cfg.isWallet !== false)).concat(GALLERY.map((g, i) => 'g' + i));
    const galleryOf = v => /^g\d+$/.test(v) ? GALLERY[+v.slice(1)] : null;
    const VIEW_LABEL = { front: 'Front', inside: 'Inside', back: 'Back', gift: 'Gift box' };
    const DEFAULT_BOX = { inside: [0.09, 0.36, 0.46, 0.62], back: [0.32, 0.30, 0.68, 0.64] };   // fractions of the photo
    const views = { current: 'front', out: {}, key: {}, seq: {}, recipes: null, sig: null,
      alts: $$('[data-vld-alt]'), lists: $$('[data-vld-views]') };
    const viewPhoto = (view, colour) => cfg.cdnBase + norm(colour) + '_' + view + '_5376-3072.jpg';
    const giftImage = () => (state.giftVariant && state.giftVariant.image) || (gift && (gift.variants.find(v => v.image) || {}).image) || null;
    const giftThumbnail = () => (state.giftVariant && state.giftVariant.thumbnail) || giftImage();
    const thumbs = view => $$('[data-vld-views] [data-view="' + view + '"]');
    const photoCache = {};
    function loadPhoto(src) {
      // CORS first (so the pixels can be read and burnt); if the host refuses, a plain image (drawable, not readable)
      if (!photoCache[src]) photoCache[src] = VLDEditor.loadImage(src).catch(() => new Promise((res, rej) => {
        const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src;
      })).catch(e => { delete photoCache[src]; throw e; });
      return photoCache[src];
    }

    function buildViews() {
      views.lists.forEach(list => {
        list.innerHTML = '';
        list.hidden = VIEWS.filter(v => v !== 'gift' || giftImage()).length < 2;   // one view: no strip
        VIEWS.forEach(v => {
          if (v === 'gift' && !giftImage()) return;
          const gal = galleryOf(v);
          const label = gal ? String(gal.label).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c])) : VIEW_LABEL[v];
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'vld-view' + (v === views.current ? ' is-on' : ''); b.dataset.view = v;
          b.setAttribute('aria-pressed', v === views.current ? 'true' : 'false');
          b.setAttribute('aria-label', (gal ? gal.label : VIEW_LABEL[v]) + ' view');
          const img = gal ? gal.thumb : v === 'gift' ? giftThumbnail() : v === 'front' ? heroUrls(state.colour).desktop : viewPhoto(v, state.colour);
          b.innerHTML = '<span class="vld-view__thumb"><img src="' + img + '" alt="">' +
            (v === 'gift' || gal ? '' : '<canvas width="192" height="144" hidden></canvas>') + '<span class="vld-view__badge"></span></span>' +
            '<span class="vld-view__label">' + label + '</span>';
          b.addEventListener('click', () => setView(v));
          list.appendChild(b);
        });
      });
      ['inside', 'back'].filter(v => VIEWS.includes(v)).forEach(v => renderView(v));
    }

    function paintThumb(view, src) {
      // cover-crop src into the view's thumbnail canvases; null = back to the plain photo
      thumbs(view).forEach(b => {
        const c = b.querySelector('canvas');
        if (!c) return;
        if (!src) { c.hidden = true; return; }
        const { w, h } = srcSize(src);
        if (!w) return;
        const k = Math.max(c.width / w, c.height / h);
        const ctx = c.getContext('2d');
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(src, (c.width - w * k) / 2, (c.height - h * k) / 2, w * k, h * k);
        c.hidden = false;
      });
    }

    function paintHero(view) {
      views.alts.forEach(alt => {
        const canvas = alt.querySelector('.vld-alt__canvas'), bg = alt.querySelector('.vld-alt__bg'), img = alt.querySelector('.vld-alt__img');
        if (view === 'gift') { img.src = giftImage() || ''; img.hidden = false; canvas.hidden = true; bg.hidden = true; return; }
        if (galleryOf(view)) { img.src = galleryOf(view).url; img.hidden = false; canvas.hidden = true; bg.hidden = true; return; }
        img.hidden = true; canvas.hidden = false; bg.hidden = false;
        const src = views.out[view];
        if (src) { copyInto(canvas, src, false); copyInto(bg, src, true); }
        else {
          canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
          bg.getContext('2d').clearRect(0, 0, bg.width, bg.height);
          img.src = viewPhoto(view, state.colour); img.hidden = false; canvas.hidden = true; bg.hidden = true;
        }
      });
    }

    function setView(v) {
      if (VIEWS.indexOf(v) < 0 || (v === 'gift' && !giftImage())) return;
      views.current = v;
      $$('[data-vld-views] .vld-view').forEach(b => { const on = b.dataset.view === v; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      [media, sheetPreview].forEach(el => el && el.classList.toggle('is-alt', v !== 'front'));
      if (v !== 'front') paintHero(v);
    }

    async function viewRecipe(view, colour) {
      try {
        if (!views.recipes) views.recipes = editor && editor.recipes ? editor.recipes() : await fetch(cfg.hub.replace(/\/$/, '') + '/pub/recipes.json').then(r => r.json());
        const byView = views.recipes.bases[String(cfg.baseId || '34')][view];
        if (!byView) return null;
        const key = Object.keys(byView).find(k => norm(k) === norm(colour));
        return key ? byView[key] : null;
      } catch (e) { return null; }
    }

    async function renderView(view) {
      if (!VIEWS.includes(view)) return;   // no inside / back on this product
      if (!window.VLDEditor || !VLDEditor.engraveText) return;
      const on = state[view], input = $('[data-addon="' + view + '"] input[type=text]');
      const text = on && input ? input.value.replace(/\s+/g, ' ').trim() : '';
      const font = (editor && editor.fontOf) ? editor.fontOf() : 'great-vibes';
      const key = [state.colour, text, font].join('|');
      thumbs(view).forEach(b => b.classList.toggle('has-text', !!text));
      if (views.key[view] === key) return;
      views.key[view] = key;
      views.out[view] = null;
      paintThumb(view, null);
      if (views.current === view) paintHero(view);
      const n = views.seq[view] = (views.seq[view] || 0) + 1;
      if (views.current === view) views.alts.forEach(a => a.classList.add('is-busy'));
      let out = null;
      try {
        const photo = viewPhoto(view, state.colour);
        if (!text) out = await loadPhoto(photo);
        else {
          const rec = await viewRecipe(view, state.colour);
          let plate = null, recipe = rec;
          if (rec && rec.plate) {
            try { plate = await VLDEditor.loadImage(cfg.hub.replace(/\/$/, '') + '/pub/plate/' + rec.plate.split('/').map(encodeURIComponent).join('/')); } catch (e) { plate = null; }
          }
          if (!plate) {
            plate = await loadPhoto(photo);
            const [fx0, fy0, fx1, fy1] = DEFAULT_BOX[view], W = plate.naturalWidth, H = plate.naturalHeight;
            recipe = Object.assign({ bbox: [W * fx0, H * fy0, W * fx1, H * fy1], burn: [0.36, 0.36, 0.36] }, rec && rec.bbox ? { bbox: rec.bbox, burn: rec.burn } : {});
          }
          out = await VLDEditor.engraveText(plate, recipe, { text: text, font: font, fill: 0.8 });
        }
      } catch (e) { out = null; }
      if (n !== views.seq[view]) return;
      if (!out) delete views.key[view]; // allow retry after a transient image failure
      views.alts.forEach(a => a.classList.remove('is-busy'));
      views.out[view] = out;
      paintThumb(view, text && out ? out : null);
      if (views.current === view) paintHero(view);
    }

    function recolourViews() {
      thumbs('front').forEach(b => { b.querySelector('img').src = heroUrls(state.colour).desktop; });
      ['inside', 'back'].forEach(v => { thumbs(v).forEach(b => { b.querySelector('img').src = viewPhoto(v, state.colour); }); renderView(v); });
    }

    // a tap on the engraved photo (hero or sheet preview) goes to that word's field; on phones the sheet opens first
    // ... and a drag on it moves that word or piece of art (phones have no other way to move things)
    function canvasPoint(c, e) {
      const r = c.getBoundingClientRect(), fit = getComputedStyle(c).objectFit;
      const k = (fit === 'cover' ? Math.max : Math.min)(r.width / c.width, r.height / c.height);
      return {fx: (e.clientX - r.left - (r.width - c.width * k) / 2) / (c.width * k), fy: (e.clientY - r.top - (r.height - c.height * k) / 2) / (c.height * k)};
    }
    function heroTap(e) {
      const c = e.currentTarget;
      if (!editor || !editor.layerAt || !c.width) return;
      const {fx, fy} = canvasPoint(c, e);
      if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return;
      const id = editor.layerAt(fx, fy);
      if (!id) return;
      if (!mq.matches && sheet && !sheet.classList.contains('is-open')) { openSheet(); setTimeout(() => editor.focusLayer(id), 400); }
      else editor.focusLayer(id);
    }
    function heroDrag(c) {
      let drag = null;
      c.addEventListener('pointerdown', e => {
        if (!editor || !editor.layerAt || !editor.moveLayer || !c.width) return;
        const p = canvasPoint(c, e);
        if (p.fx < 0 || p.fx > 1 || p.fy < 0 || p.fy > 1) return;
        const id = editor.layerAt(p.fx, p.fy);
        if (!id) return;
        drag = {id: id, last: p, startX: e.clientX, startY: e.clientY, moved: false};
        try { c.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
      });
      c.addEventListener('pointermove', e => {
        if (!drag) return;
        if (!drag.moved && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 6) return;   // a tap so far
        drag.moved = true;
        const p = canvasPoint(c, e);
        editor.moveLayer(drag.id, p.fx - drag.last.fx, p.fy - drag.last.fy, false);
        drag.last = p;
        e.preventDefault();
      });
      const end = e => {
        if (!drag) return;
        const d = drag; drag = null;
        if (d.moved) { editor.moveLayer(d.id, 0, 0, true); c.dataset.vldDragged = '1'; setTimeout(() => { delete c.dataset.vldDragged; }, 50); }
      };
      c.addEventListener('pointerup', end); c.addEventListener('pointercancel', end);
      c.addEventListener('click', e => { if (c.dataset.vldDragged) { e.stopImmediatePropagation(); e.preventDefault(); } }, true);
      c.addEventListener('click', heroTap);
    }
    if (sheetCanvas) heroDrag(sheetCanvas);

    // ---------- the panel and the buy block live in the column on desktop, in the sheet on phones ----------
    const panel = $('.vld-panel'), buy = $('.vld-buy'), sheet = $('.vld-sheet'), addons = $('[data-vld-addons]'), meta = $('.vld-meta');
    let placedMobile = false;
    function placePanels() {
      const desk = mq.matches;
      const panelSlot = desk ? $('[data-vld-slot="panel-desktop"]') : $('[data-vld-slot="panel-mobile"]');
      const buySlot = desk ? $('[data-vld-slot="buy-desktop"]') : $('[data-vld-slot="buy-mobile"]');
      if (panelSlot && panel.parentNode !== panelSlot) panelSlot.appendChild(panel);
      if (buySlot && buy.parentNode !== buySlot) buySlot.appendChild(buy);
      // add-ons: always open (no summary row) in the column; one collapsed row in the sheet until the customer opens it
      if (addons) { if (desk) addons.open = true; else if (!placedMobile) addons.open = false; }
      // the small print: under the button in the column, at the end of the scrolling fields in the sheet (its footer keeps total + button)
      if (meta) { const dest = desk ? buy : $('.vld-sheet__content'); if (dest && meta.parentNode !== dest) dest.appendChild(meta); }
      if (!desk) placedMobile = true;
      if (desk) closeSheet();
    }
    // the sheet is a modal dialog: focus starts on Back, Tab/Shift+Tab stay inside, Escape closes, focus returns to the opener
    const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let sheetOpener = null;
    const sheetFocusables = () => $$(FOCUSABLE, sheet).filter(el => el.offsetParent !== null || el === document.activeElement);
    function openSheet(e) {
      if (!sheet) return;
      sheetOpener = (e && e.currentTarget && e.currentTarget.focus) ? e.currentTarget : document.activeElement;
      sheet.classList.add('is-open'); sheet.setAttribute('aria-hidden', 'false'); sheet.setAttribute('aria-modal', 'true');
      document.body.classList.add('vld-sheet-open');
      mirrorCanvas();
      const back = $('[data-vld-close-sheet]', sheet) || sheetFocusables()[0];
      if (back) setTimeout(() => back.focus({ preventScroll: true }), 50);
    }
    function closeSheet() {
      if (!sheet) return;
      const wasOpen = sheet.classList.contains('is-open');
      sheet.classList.remove('is-open'); sheet.setAttribute('aria-hidden', 'true'); sheet.removeAttribute('aria-modal');
      document.body.classList.remove('vld-sheet-open');
      if (wasOpen && sheetOpener && sheetOpener.focus && document.contains(sheetOpener)) sheetOpener.focus({ preventScroll: true });
      sheetOpener = null;
    }
    $$('[data-vld-open-sheet]').forEach(b => b.addEventListener('click', openSheet));
    $$('[data-vld-close-sheet]').forEach(b => b.addEventListener('click', closeSheet));
    // "Personalise" in the value sections: scroll to the editor column on desktop, open the sheet on phones
    $$('[data-vld-personalise]').forEach(b => b.addEventListener('click', e => {
      e.preventDefault();
      if (mq.matches) {
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const first = panel && $('textarea, input[type=text]', panel);
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 450);
        return;
      }
      const opener = $$('[data-vld-open-sheet]').find(el => el.offsetParent !== null);
      if (opener) opener.click(); else openSheet(e);
    }));
    document.addEventListener('keydown', e => {
      if (!sheet || !sheet.classList.contains('is-open')) return;
      if (e.key === 'Escape') { closeSheet(); return; }
      if (e.key !== 'Tab') return;
      const f = sheetFocusables();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1], inside = sheet.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    });
    // focus that still escapes (e.g. screen-reader navigation) is pulled back in
    document.addEventListener('focusin', e => {
      if (sheet && sheet.classList.contains('is-open') && !sheet.contains(e.target) && !mq.matches) {
        const f = sheetFocusables(); if (f.length) f[0].focus({ preventScroll: true });
      }
    });
    (mq.addEventListener ? mq.addEventListener('change', placePanels) : mq.addListener(placePanels));
    placePanels();

    // ---------- mobile bar: tighter once the hero has scrolled away ----------
    const bar = $('.lux-mobile-bar');
    if (bar) {
      const mediaEl = $('.lux-pdp__media');
      const update = () => bar.classList.toggle('is-scrolled', window.scrollY > (mediaEl ? mediaEl.offsetHeight * 0.125 : 125));
      window.addEventListener('scroll', update, { passive: true });
      update();
    }

    // ---------- lightbox for the craftsmanship tiles ----------
    const lightbox = $('.lux-craft-lightbox');
    if (lightbox) {
      const img = $('.lux-craft-lightbox__img', lightbox), lbl = $('.lux-craft-lightbox__label', lightbox);
      const close = () => { lightbox.classList.remove('is-active'); document.body.style.overflow = ''; };
      root.addEventListener('click', e => {
        const t = e.target.closest('.lux-craftsmanship__img');
        if (!t) return;
        img.src = t.src;
        lbl.textContent = (t.closest('.lux-craftsmanship__item') || {}).querySelector ? t.closest('.lux-craftsmanship__item').querySelector('.lux-craftsmanship__label').textContent : '';
        lightbox.classList.add('is-active'); document.body.style.overflow = 'hidden';
      });
      $('.lux-craft-lightbox__close', lightbox).addEventListener('click', e => { e.stopPropagation(); close(); });
      lightbox.addEventListener('click', e => { if (e.target === lightbox) close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && lightbox.classList.contains('is-active')) close(); });
    }

    // ---------- the editor ----------
    async function mountEditor() {
      const slot = $('.vld-editor-slot');
      if (!slot || !cfg.designId || !window.VLDEditor) {
        if (slot) { slot.classList.add('is-failed'); slot.innerHTML = '<p class="vld-editor-fallback">This design is engraved exactly as shown in the photo. Add a message inside or on the back below.</p>'; }
        return;
      }
      const edRoot = document.createElement('div');
      slot.appendChild(edRoot);
      try {
        editor = await window.VLDEditor.mount(edRoot, {
          hub: cfg.hub, designId: +cfg.designId, base: String(cfg.baseId || '34'),
          colour: state.colour, backdrop: cfg.backdrop || undefined,
          onColour: c => selectColour(c),
          onChange: s => {
            if (!editorLive) {
              editorLive = true;
              if (media) media.classList.add('is-live');
              if (sheetPreview) sheetPreview.classList.add('is-live');
            }
            mirrorCanvas();
            // the front changed (words, font, size, position, a new line): show the front again; a colour change does not count
            const sig = JSON.stringify(s.overrides.layers) + '|' + (s.added || []).length;
            if (views.sig !== null && sig !== views.sig && views.current !== 'front') setView('front');
            if (sig !== views.sig) { views.sig = sig; ['inside', 'back'].forEach(v => renderView(v)); }   // a font change re-engraves the inside / back
          },
        });
        views.recipes = editor.recipes ? editor.recipes() : views.recipes;
        // the engraved photo becomes the hero: move the stage into the media column
        const stage = edRoot.querySelector('.vld-ed__stage');
        if (stage && stageSlot) stageSlot.appendChild(stage);
        const mainCanvas = stage && stage.querySelector('.vld-ed__product');
        if (mainCanvas) heroDrag(mainCanvas);
        mirrorCanvas();   // the first draw happened before the stage was here: backdrop, sheet preview and Front thumbnail catch up
        new MutationObserver(() => stageSlot && stageSlot.classList.toggle('is-busy', edRoot.classList.contains('is-busy')))
          .observe(edRoot, { attributes: true, attributeFilter: ['class'] });
        // the recipe colour may differ in case from the option value: align now that the editor exists
        const rc = recipeColour(state.colour);
        if (rc !== state.colour) editor.setProduct({ colour: rc });
        try { fontsCat = await window.VLD.fonts(cfg.hub.replace(/\/$/, '') + '/pub/fonts.json'); } catch (e) { fontsCat = null; }
      } catch (e) {
        slot.classList.add('is-failed');
        edRoot.innerHTML = '<p class="vld-editor-fallback">The live preview could not load. Your ' + (cfg.noun || 'wallet') + ' will be engraved with the design in the photo.</p>';
        editor = null;
      }
    }

    // ---------- add to cart ----------
    function showError(msg) { $$('[data-vld-err]').forEach(e => { e.textContent = msg; e.hidden = !msg; }); }

    async function addToCart() {
      showError('');
      const variant = findVariant(state.colour, state.inside, state.back);
      if (!variant) return showError('That combination is not available. Please choose another colour.');
      if (!variant.available) return showError('This option is sold out right now.');
      const props = {};
      if (editor) {
        const s = editor.state();
        const texts = Object.values(s.texts || {}).map(t => String(t).replace(/\s+/g, ' ').trim()).filter(Boolean);
        if (texts.length) props['Front'] = texts.join(' / ').slice(0, 255);
        const fonts = [];
        Object.values(s.overrides.layers || {}).forEach(o => { if (o.font) fonts.push(fontsCat && fontsCat.fonts[o.font.split(':')[0]] ? fontsCat.fonts[o.font.split(':')[0]].name : o.font); });
        if (fonts.length) props['Font'] = Array.from(new Set(fonts)).join(', ');
        props['_design_id'] = String(s.design_id);
        props['_design_version'] = String(s.design_version || '');
        if (cfg.comboId) props['_combo_id'] = String(cfg.comboId);
        props['_overrides'] = JSON.stringify({ layers: s.overrides.layers, added: s.added });
        // the hosted preview must match the editor: same added lines, same design version (KB 20 P1)
        const q = new URLSearchParams({ o: JSON.stringify({ layers: s.overrides.layers, added: s.added || [] }), base: s.product.base,
          backdrop: s.product.backdrop, colour: s.product.colour, w: '800' });
        if (s.design_version) q.set('v', String(s.design_version));
        props['_preview_url'] = cfg.hub.replace(/\/$/, '') + '/pub/engraved/' + s.design_id + '.jpg?' + q;
      } else {
        props['_design_id'] = String(cfg.designId || '');
        if (cfg.comboId) props['_combo_id'] = String(cfg.comboId);
      }
      const insideText = ($('[data-addon="inside"] input[type=text]') || {}).value || '';
      const backText = ($('[data-addon="back"] input[type=text]') || {}).value || '';
      if (state.inside) { if (!insideText.trim()) return showError('Add your inside message, or untick inside engraving.'); props['Inside Engraving'] = insideText.trim(); }
      if (state.back) { if (!backText.trim()) return showError('Add your back engraving, or untick it.'); props['Back Engraving'] = backText.trim(); }
      const items = [{ id: variant.id, quantity: 1, properties: props }];
      if (state.gift && state.giftVariant) {
        props['Gift Box'] = state.giftVariant.title;
        items.push({ id: state.giftVariant.id, quantity: 1, properties: { 'For': cfg.productTitle } });
      }
      $$('[data-vld-atc]').forEach(b => { b.disabled = true; b.classList.add('is-shimmer'); });
      try {
        const body = { items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname };
        const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        $$('[data-vld-voila]').forEach(e => e.classList.add('on'));
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { variantId: variant.id, items: items } }));
        setTimeout(() => {
          closeSheet();
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections && data.sections['cart-drawer']) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else if (!cfg.harness) {
            window.location.href = '/cart';
          }
          $$('[data-vld-atc]').forEach(b => { b.classList.remove('is-shimmer'); });
          refresh();
        }, 1000);
      } catch (e) {
        showError(e.message || 'Could not add to cart. Please try again.');
        $$('[data-vld-atc]').forEach(b => { b.classList.remove('is-shimmer'); });
        refresh();
      }
    }
    $$('[data-vld-atc]').forEach(b => b.addEventListener('click', addToCart));

    // ---------- go ----------
    renderSwatches();
    renderOptions();
    updateCraftsmanship(state.colour);
    buildViews();
    refresh();
    document.dispatchEvent(new CustomEvent('luxury:variantChange', { detail: { variant: findVariant(state.colour, false, false), colour: state.colour } }));
    mountEditor();

    // test hook (harness only)
    root.__vld = { state: state, selectColour: selectColour, openSheet: openSheet, closeSheet: closeSheet, refresh: refresh,
      setView: setView, renderView: renderView, views: views,
      get editor() { return editor; }, get live() { return editorLive; } };
  }

  function boot() { document.querySelectorAll('[data-vld-product]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
